import Paragraph from 'components/Paragraph';
import { Document } from 'prismic-javascript/src/documents';
import { Elements, RichText } from 'prismic-reactjs';
import React, { ReactNode } from 'react';

const CSS = require('./DefaultPrismic.less');

type Slice = {
  slice_type: string;
  slice_label: string;
  value: any[];
};

export type InterlinkingBlockProps = {
  title: string;
  linkColumns: InterlinkingBlockColumn[];
};

type InterlinkingBlockColumn = { title: string; url: string }[];

export type Banner = {
  title: string;
  description: ReactNode[];
  image: string;
};

export type Article = {
  title: string;
  content: ReactNode[];
};

const propsWithUniqueKey = (props, key) => {
  return Object.assign(props || {}, { key });
};

// adapted from the prismic docs found at https://prismic.io/docs/reactjs/beyond-the-api/html-serializer
const htmlSerializer = (type, element, content, children, key) => {
  let props = {};
  switch (type) {
    case Elements.paragraph:
      props = { className: CSS.Paragraph };
      return (
        <Paragraph {...propsWithUniqueKey(props, key)}>{children}</Paragraph>
      );
    case Elements.hyperlink:
      const targetAttr = element.data.target
        ? { target: element.data.target }
        : {};
      const relAttr = element.data.target ? { rel: 'noopener' } : {};
      props = Object.assign(
        {
          className: CSS.Anchor,
          href: element.data.url,
        },
        targetAttr,
        relAttr,
      );

      return <a {...propsWithUniqueKey(props, key)}>{children}</a>;
  }
  return null;
};

class PrismicResponseParser {
  static parseBannerSlice = (slice: Slice): Banner => {
    const sliceValue = slice.value[0];
    const title = sliceValue.title[0].text;

    const description = [
      <RichText
        htmlSerializer={htmlSerializer}
        key={`${title}`}
        render={sliceValue.description}
      />,
    ];

    const image = sliceValue.background;

    return {
      title,
      description,
      image,
    };
  };

  static parseArticleSlice = (slice: Slice): Article => {
    const sliceValue = slice.value[0];
    const title = sliceValue.title;

    return {
      title,
      content: [<RichText key="0" render={sliceValue.content} />],
    };
  };

  static parseInterlinkingDocument = (
    document: Document,
  ): InterlinkingBlockProps => {
    const title = document.data.title;
    const interlinkingDocumentContentLinks = document.data.links.map(
      (linkData) => {
        const skippableIndices = [];
        return Object.keys(linkData).reduce((result, link) => {
          const isUrl = link.startsWith('url');
          const isTitle = link.startsWith('title');

          if (isUrl || isTitle) {
            const textIndex = link.split(isUrl ? 'url' : 'title')[1];
            const index = parseInt(textIndex, 10);
            if (skippableIndices.includes(index)) {
              return result;
            }
            skippableIndices.push(index);
            const title = linkData[`title${index}`];
            const urlData = linkData[`url${index}`];

            if (!title || !urlData) {
              return result;
            }

            let url = urlData.url;
            if (!url) {
              return result;
            }
            url = url.endsWith('/') ? url : `${url}/`;

            result.splice(index, 0, { url, title });
          }
          return result;
        }, []);
      },
    );
    return { title, linkColumns: interlinkingDocumentContentLinks };
  };
}

export default PrismicResponseParser;
